import React from 'react';
import * as D3 from 'd3';

import {
    getUnitForScoring,
    roundToPrecision,
    formatValueWithUnit,
    normalizeDegreeRange,
    normalizeActualDegreeValue,
} from 'ff/debrief/utils';

interface Props {
    goalMin: number | null;
    goalMax: number | null;
    actualMin?: number;
    actualMax?: number;
    size?: 'sm' | 'lg';
    label?: string;
    goal?: string;
    speedUnit?: string;
}

const ScoreRange = (props: React.PropsWithChildren<Props>) => {
    const {
        goalMin: goalMinRaw,
        goalMax: goalMaxRaw,
        size,
        label,
        goal,
        speedUnit,
    } = props;
    const svgRef = React.useRef<SVGSVGElement>(null);
    const [dimensions, setDimensions] = React.useState({
        width: 0,
        height: size === 'lg' ? 60 : 15,
    });

    // use rounded values for display for consistency with score deviation
    const { unit } = getUnitForScoring(0, label || '', goal || '', speedUnit);
    let actualMin =
        props.actualMin !== undefined
            ? roundToPrecision(props.actualMin, unit)
            : undefined;
    let actualMax =
        props.actualMax !== undefined
            ? roundToPrecision(props.actualMax, unit)
            : undefined;

    React.useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                setDimensions(prev => ({ ...prev, width }));
            }
        });

        if (svgRef.current) {
            resizeObserver.observe(svgRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const height = dimensions.height;
    const bracketHeight = size === 'lg' ? 24 : 12;
    const padding = dimensions.width * 0.15;
    const fixedBarWidth = dimensions.width * 0.05; // Fixed width for out-of-bounds bars
    const edgeMargin = dimensions.width * 0.08; // Margin from the edge for out-of-bounds indicators

    // Convert from kts to mph if needed
    const convertValue = (value: number | null) => {
        if (value == null) {
            return value;
        }
        return getUnitForScoring(value, label || '', goal || '', speedUnit)
            .value;
    };

    actualMin = convertValue(actualMin);
    actualMax = convertValue(actualMax);
    const goalMin = convertValue(goalMinRaw);
    const goalMax = convertValue(goalMaxRaw);

    // Detect unit and normalize wrapped degree ranges
    const goalNormalized = normalizeDegreeRange(goalMin, goalMax, unit);

    // Normalize values for calculations
    const normalizedActualMin = normalizeActualDegreeValue(
        actualMin,
        goalMin,
        goalNormalized.isWrapped
    );
    const normalizedActualMax = normalizeActualDegreeValue(
        actualMax,
        goalMin,
        goalNormalized.isWrapped
    );
    const normalizedGoalMin = goalNormalized.normalizedMin;
    const normalizedGoalMax = goalNormalized.normalizedMax;

    // Check for open-ended ranges
    const isOpenMin = goalMin === null;
    const isOpenMax = goalMax === null;

    // Determine the domain for the scale
    let domainMin: number, domainMax: number;

    if (isOpenMin && isOpenMax) {
        // Both ends open - use actual values with padding
        domainMin =
            (normalizedActualMin ?? 0) -
            Math.abs((normalizedActualMax ?? 0) - (normalizedActualMin ?? 0)) *
                0.2;
        domainMax =
            (normalizedActualMax ?? 0) +
            Math.abs((normalizedActualMax ?? 0) - (normalizedActualMin ?? 0)) *
                0.2;
    } else if (isOpenMin) {
        // Only min is open
        domainMin =
            Math.min(
                normalizedActualMin ?? normalizedGoalMax ?? 0,
                normalizedGoalMax ?? 0
            ) -
            Math.abs((normalizedGoalMax ?? 0) - (normalizedActualMin ?? 0)) *
                0.2;
        domainMax = normalizedGoalMax ?? 0;
    } else if (isOpenMax) {
        // Only max is open
        domainMin = normalizedGoalMin ?? 0;
        domainMax =
            Math.max(
                normalizedActualMax ?? normalizedGoalMin ?? 0,
                normalizedGoalMin ?? 0
            ) +
            Math.abs((normalizedActualMax ?? 0) - (normalizedGoalMin ?? 0)) *
                0.2;
    } else {
        // Both ends closed
        domainMin = normalizedGoalMin ?? 0;
        domainMax = normalizedGoalMax ?? 0;
    }

    // Set up D3 scale
    const xScale = D3.scaleLinear()
        .domain([domainMin, domainMax])
        .range([padding, dimensions.width - padding])
        .clamp(false); // Allow values outside the domain

    // Determine if actual values are out of bounds
    const actualMinOutOfVisualRange =
        !isOpenMin &&
        normalizedActualMin !== undefined &&
        xScale(normalizedActualMin) < 0;
    const actualMaxOutOfVisualRange =
        !isOpenMax &&
        normalizedActualMax !== undefined &&
        xScale(normalizedActualMax) > dimensions.width;
    const bothBelowGoal =
        !isOpenMin &&
        normalizedActualMin !== undefined &&
        normalizedActualMax !== undefined &&
        normalizedActualMax < normalizedGoalMin;
    const bothAboveGoal =
        !isOpenMax &&
        normalizedActualMin !== undefined &&
        normalizedActualMax !== undefined &&
        normalizedActualMin > normalizedGoalMax;

    // Calculate bar positions based on out-of-bounds status
    let barX1: number, barX2: number;
    let useFixedWidth = false;

    if (bothBelowGoal) {
        // Case 3: Both values are too small
        useFixedWidth = true;
        barX1 = edgeMargin;
        barX2 = barX1 + fixedBarWidth;
    } else if (bothAboveGoal) {
        // Case 4: Both values are too large
        useFixedWidth = true;
        barX2 = dimensions.width - edgeMargin;
        barX1 = barX2 - fixedBarWidth;
    } else if (actualMinOutOfVisualRange && !actualMaxOutOfVisualRange) {
        // Case 1: Min is too small, max is within range
        barX1 = edgeMargin;
        barX2 = xScale(normalizedActualMax);
    } else if (!actualMinOutOfVisualRange && actualMaxOutOfVisualRange) {
        // Case 2: Max is too large, min is within range
        barX1 = xScale(normalizedActualMin);
        barX2 = dimensions.width - edgeMargin;
    } else {
        // Normal case: both values are within visible range
        barX1 = xScale(normalizedActualMin);
        barX2 = xScale(normalizedActualMax);
    }

    const belowMinValue =
        !isOpenMin && normalizedActualMin < normalizedGoalMin
            ? normalizedActualMin
            : normalizedGoalMin;
    const aboveMaxValue =
        !isOpenMax && normalizedActualMax > normalizedGoalMax
            ? normalizedActualMax
            : normalizedGoalMax;
    let withinRangeValues = [
        !isOpenMin && normalizedActualMin < normalizedGoalMin
            ? normalizedGoalMin
            : normalizedActualMin,
        !isOpenMax && normalizedActualMax > normalizedGoalMax
            ? normalizedGoalMax
            : normalizedActualMax,
    ];

    // Check if entirely out of goal range
    if (
        !isOpenMax &&
        normalizedActualMin > normalizedGoalMax &&
        normalizedActualMax > normalizedGoalMax
    ) {
        withinRangeValues = [normalizedGoalMax, normalizedGoalMax];
    } else if (
        !isOpenMin &&
        normalizedActualMin < normalizedGoalMin &&
        normalizedActualMax < normalizedGoalMin
    ) {
        withinRangeValues = [normalizedGoalMin, normalizedGoalMin];
    }

    // Compute bracket paths
    const bracketTop = (height - bracketHeight) / 2;
    const bracketBottom = (height + bracketHeight) / 2;

    const leftBracketPath = `M ${padding} ${bracketTop} L ${padding} ${bracketBottom}`;
    const midpointBracketPath = `M ${dimensions.width / 2} ${bracketTop} L ${dimensions.width / 2} ${bracketBottom}`;
    const rightBracketPath = `M ${dimensions.width - padding} ${bracketTop} L ${dimensions.width - padding} ${bracketBottom}`;

    const actualMinPath = `M ${xScale(normalizedActualMin)} ${bracketTop + 5} L ${xScale(normalizedActualMin)} ${bracketBottom - 5}`;
    const actualMaxPath = `M ${xScale(normalizedActualMax)} ${bracketTop + 5} L ${xScale(normalizedActualMax)} ${bracketBottom - 5}`;

    const formatValue = (value: number) => formatValueWithUnit(value, unit);

    // Calculate label positions based on bar positions
    const minLabelDistance = 60; // Minimum pixels between label centers
    const actualMinX =
        useFixedWidth || actualMinOutOfVisualRange
            ? barX1
            : xScale(normalizedActualMin);
    const actualMaxX =
        useFixedWidth || actualMaxOutOfVisualRange
            ? barX2
            : xScale(normalizedActualMax);
    const labelsWouldOverlap =
        Math.abs(actualMaxX - actualMinX) < minLabelDistance;

    // Determine which labels to show
    const showBothLabels = props.size === 'lg' && !labelsWouldOverlap;
    const showMinLabel =
        props.size === 'lg' &&
        (showBothLabels || actualMin === actualMax) &&
        actualMin !== actualMax;
    const showMaxLabel =
        props.size === 'lg' && showBothLabels && actualMin !== actualMax;

    return (
        <svg
            ref={svgRef}
            width="100%"
            height={height}
            style={{ overflow: 'hidden' }}
        >
            {/* Horizontal line connecting brackets */}
            <line
                x1={padding}
                y1={height / 2}
                x2={dimensions.width - padding}
                y2={height / 2}
                stroke="black"
                strokeWidth="1"
            />

            {/* Main bar - red if out of bounds, otherwise colored based on goal compliance */}
            {/* Render bar as tick if the actual value is a single point */}
            {normalizedActualMax === normalizedActualMin ? (
                <rect
                    x={Math.min(
                        Math.max(xScale(normalizedActualMin), edgeMargin),
                        dimensions.width - edgeMargin
                    )}
                    y={height / 2 - bracketHeight / 4}
                    height={bracketHeight / 2}
                    width="2px"
                    fill={
                        (normalizedActualMin > normalizedGoalMin ||
                            normalizedGoalMin === null) &&
                        (normalizedActualMin < normalizedGoalMax ||
                            normalizedGoalMax === null)
                            ? '#4CAF50'
                            : '#DA2F09'
                    }
                />
            ) : useFixedWidth ||
              actualMinOutOfVisualRange ||
              actualMaxOutOfVisualRange ? (
                <>
                    {/* Out of bounds - render red bar at calculated position */}
                    <Rect
                        x1={barX1}
                        x2={barX2}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#DA2F09"
                    />
                    {/* Render green bar for in range values */}
                    <Rect
                        x1={xScale(withinRangeValues[0])}
                        x2={xScale(withinRangeValues[1])}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#4CAF50"
                    />
                </>
            ) : (
                <>
                    {/* Green bar from min to actual value (if within range) */}
                    <Rect
                        x1={xScale(withinRangeValues[0])}
                        x2={xScale(withinRangeValues[1])}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#4CAF50"
                    />

                    {/* Warning bar from actual min to goal min (if exceeding) */}
                    <Rect
                        x1={xScale(belowMinValue)}
                        x2={xScale(
                            Math.min(normalizedActualMax, normalizedGoalMin)
                        )}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#DA2F09"
                    />

                    {/* Warning bar from goal max to actual max (if exceeding) */}
                    <Rect
                        x1={xScale(
                            Math.max(normalizedActualMin, normalizedGoalMax)
                        )}
                        x2={xScale(aboveMaxValue)}
                        y={height / 2 - bracketHeight / 4}
                        height={bracketHeight / 2}
                        fill="#DA2F09"
                    />
                </>
            )}

            {/* Min value label - only show if not open-ended */}
            {props.size === 'lg' && !isOpenMin && (
                <text
                    x={padding}
                    y={height / 2 + bracketHeight / 2 + 15}
                    textAnchor="middle"
                    fontSize="12"
                    fill="black"
                >
                    {formatValue(goalMin)}
                </text>
            )}

            {/* Max value label - only show if not open-ended */}
            {props.size === 'lg' && !isOpenMax && (
                <text
                    x={dimensions.width - padding}
                    y={height / 2 + bracketHeight / 2 + 15}
                    textAnchor="middle"
                    fontSize="12"
                    fill="black"
                >
                    {formatValue(goalMax)}
                </text>
            )}

            {actualMin === actualMax && (
                <>
                    <text
                        x={Math.min(
                            Math.max(xScale(normalizedActualMin), edgeMargin),
                            dimensions.width - edgeMargin
                        )}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMin)}
                    </text>
                </>
            )}

            {/* Actual min label */}
            {showMinLabel && (
                <>
                    <text
                        x={Math.max(actualMinX, edgeMargin)}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMin)}
                    </text>
                    {!useFixedWidth && !actualMinOutOfVisualRange && (
                        <path
                            d={actualMinPath}
                            stroke="black"
                            strokeWidth="1"
                            fill="none"
                        />
                    )}
                </>
            )}

            {/* Actual max label */}
            {showMaxLabel && (
                <>
                    <text
                        x={Math.min(actualMaxX, dimensions.width - edgeMargin)}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMax)}
                    </text>
                    {!useFixedWidth && !actualMaxOutOfVisualRange && (
                        <path
                            d={actualMaxPath}
                            stroke="black"
                            strokeWidth="1"
                            fill="none"
                        />
                    )}
                </>
            )}

            {/* Combined label when values are too close */}
            {props.size === 'lg' &&
                labelsWouldOverlap &&
                actualMin !== actualMax && (
                    <text
                        x={(actualMinX + actualMaxX) / 2}
                        y={height / 2 - bracketHeight / 2 - 5}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        {formatValue(actualMin)} &nbsp;&nbsp;{' '}
                        {formatValue(actualMax)}
                    </text>
                )}

            {/* Left bracket - only show if not open-ended */}
            {!isOpenMin && (
                <path
                    d={leftBracketPath}
                    stroke="black"
                    strokeWidth="1"
                    fill="none"
                />
            )}

            {/* mid-point bracket - only show if not open-ended */}
            {!isOpenMin && !isOpenMax && (
                <path
                    d={midpointBracketPath}
                    stroke="black"
                    strokeWidth="1"
                    fill="none"
                />
            )}

            {/* Right bracket - only show if not open-ended */}
            {!isOpenMax && (
                <path
                    d={rightBracketPath}
                    stroke="black"
                    strokeWidth="1"
                    fill="none"
                />
            )}
        </svg>
    );
};

const Rect = (
    props: React.PropsWithChildren<
        { x1: number; x2: number } & React.SVGProps<SVGRectElement>
    >
) => {
    const { x1, x2, children, ...rectProps } = props;
    const width = Math.abs(x2 - x1);

    return (
        <rect {...rectProps} x={x1} y={rectProps.y || 0} width={width}>
            {children}
        </rect>
    );
};

export default ScoreRange;
